import { UserState } from './UserState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { IProfileCollectionData } from './ProfileCollectionForm';
import {
    TOS_CONSENT_VERSION,
    TOS_COMBINED_CONSENT_VERSION,
    SMS_CONSENT_VERSION,
    SMS_COMBINED_CONSENT_VERSION,
    SMS_CONSENT_SAVE_TEXT,
    COMBINED_CONSENT_SAVE_TEXT,
    ACCEPT_TERMS_AND_SKIP_TEXT
} from './consentText';

const SKIPPED_THIS_SESSION_KEY = 'ih:profile-check-skipped';
const LOCATION = 'require-profile-check';

// What the profile check does with the reader's answers: saves them with the consents its buttons
// promise, or puts the check off for this session.
export class ProfileCheckService {
    static inject = () => [UserState, MixPanelEventEmitter];
    constructor(private userState: UserState, private mixpanel: MixPanelEventEmitter) {}

    wasSkippedThisSession = (): boolean => Boolean(sessionStorage.getItem(SKIPPED_THIS_SESSION_KEY));

    trackViewed = (): void => {
        this.mixpanel.track('Profile Collection Viewed', () => ({ Location: LOCATION }));
    };

    // What Save consents to, as shown under it and as recorded: texts alone once the Terms are accepted.
    getSaveConsentText = (hasTosConsent: boolean): string => hasTosConsent ? SMS_CONSENT_SAVE_TEXT : COMBINED_CONSENT_SAVE_TEXT;

    save = async ({ data, hasTosConsent }: { data: IProfileCollectionData; hasTosConsent: boolean; }): Promise<void> => {
        const cleanPhone = data.phone.replace(/\D/g, '');
        const existingPhone = this.userState.state.phone;
        const disclosureText = this.getSaveConsentText(hasTosConsent);

        if (data.firstName) {
            this.userState.setFirstName(data.firstName);
        }
        if (data.lastName) {
            this.userState.setLastName(data.lastName);
        }
        if (data.location) {
            this.userState.setAddress(data.location);
        }
        if (data.phone) {
            this.userState.setPhone(cleanPhone);
        }

        if (!hasTosConsent) {
            this.userState.addConsent({
                consentType: 'terms-of-service',
                consentedAt: new Date().toISOString(),
                disclosureVersion: TOS_COMBINED_CONSENT_VERSION,
                disclosureText
            });
        }

        const phoneChanged = cleanPhone && cleanPhone !== existingPhone;
        if (phoneChanged) {
            this.userState.addConsent({
                consentType: 'sms-consent',
                consentedAt: new Date().toISOString(),
                disclosureVersion: hasTosConsent ? SMS_CONSENT_VERSION : SMS_COMBINED_CONSENT_VERSION,
                disclosureText,
                phone: cleanPhone
            });
        }

        await this.userState.save();

        this.mixpanel.track('Profile Collection Completed', () => ({
            Location: LOCATION,
            'Fields Entered': [
                data.firstName ? 'firstName' : '',
                data.lastName ? 'lastName' : '',
                // still reported as zipCode so the existing reports keep counting the same field
                data.location ? 'zipCode' : '',
                data.phone ? 'phone' : ''
            ].filter(Boolean)
        }));
    };

    skip = (): void => {
        sessionStorage.setItem(SKIPPED_THIS_SESSION_KEY, 'true');
        this.mixpanel.track('Profile Collection Skipped', () => ({ Location: LOCATION }));
    };

    // for someone who cannot or will not give a mobile number, which Save requires along with consent to texts
    acceptTermsAndSkip = async (): Promise<void> => {
        this.userState.addConsent({
            consentType: 'terms-of-service',
            consentedAt: new Date().toISOString(),
            disclosureVersion: TOS_CONSENT_VERSION,
            disclosureText: ACCEPT_TERMS_AND_SKIP_TEXT
        });
        await this.userState.save();

        sessionStorage.setItem(SKIPPED_THIS_SESSION_KEY, 'true');
        this.mixpanel.track('Profile Collection Skipped', () => ({ Location: LOCATION, 'Accepted Terms': true }));
    };
}
